import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { observer } from 'mobx-react-lite';
import { ApplicationSdk } from "../../../stores";
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dropdown } from 'primereact/dropdown';
import UploadImageDialog from "../upload/UploadImageDialog";

const DialogFooter = observer(({ onSave, store }: any) => {
    return <div className="flex justify-content-between align-items-center">
        <Button
            label="Frage hinzufügen"
            icon="pi pi-plus"
            disabled={store.isNewTraining || store.isLoading}
            onClick={() => store.onAddQuestion()} // Öffnet das Produkt-Auswahl-Dropdown
        />
        {/* Speichern Button */}
        <div>
            <Button
                label={store.buttonLabel}
                icon="pi pi-check"
                className="p-button-success"
                disabled={!store.isValid || store.isLoading}
                onClick={() => {
                    onSave();
                }}
            />
        </div>
    </div>
});

const TrainingVideoUrl = observer(({ store }: { store: any }) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        store.trainingVideo = e.target.value;
    };

    return (
        <div style={{ marginBottom: "1rem", width: "100%" }}>
            <label
                htmlFor="trainingVideo"
                style={{ display: "block", marginBottom: "0.5rem" }}
            >
                Video-Link
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <InputText
                    id="videoUrl"
                    placeholder="Fügen Sie einen Video-Link ein"
                    value={store.trainingVideo}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                    className={store.isTrainingVideoValid ? "" : "p-invalid"}
                />
                <Button
                    icon="pi pi-external-link"
                    className="p-button-success"
                    tooltip="Link in neuem Tab öffnen"
                    disabled={!store.isTrainingVideoValid}
                    onClick={() => store.openVideoUrl()}
                />
            </div>
        </div>
    );
});

const TrainingName = observer(({ store }: { store: any }) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= 80) {
            store.trainingName = e.target.value;
        }
    };

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label htmlFor="trainingName" style={{ display: 'block', marginBottom: '0.5rem' }}>Schulung-Name</label>
            <InputText
                id="trainingName"
                placeholder="Schulungsname eingeben"
                value={store.trainingName}
                onChange={handleInputChange}
                style={{ width: '100%' }}
                data-pr-tooltip="Der Schulungsname muss mindestens 3 Zeichen lang sein."
                data-pr-position="top"
                data-pr-disabled={store.isTrainingNameValid}
                invalid={!store.isTrainingNameValid}
            />
            <Tooltip target="#trainingName" />
        </div>
    );
});


const TrainingDescription = observer(({ store }: { store: any }) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        store.trainingDescription = e.target.value;
    };

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label htmlFor="trainingDescription" style={{ display: 'block', marginBottom: '0.5rem' }}>Schulung-Beschreibung</label>
            <InputTextarea
                id="trainingDescription"
                placeholder="Beschreibug eingeben"
                value={store.trainingDescription}
                onChange={handleInputChange}
                rows={5}
                cols={30}
                style={{ width: '100%' }}
            />
        </div>
    );
});

const TrainingQuestionEditorFooter = observer(({ onSave, store }: any) => {
    return <div className="flex justify-content-between align-items-center">
        {/* Produkt hinzufügen Button */}
        <Button
            label="Antwort hinzufügen"
            icon="pi pi-plus"
            disabled={store.editor?.maximumAnswersReached} // Disable if maximum answers reached
            onClick={() => store.editor?.addAnswer()} // Add a new answer to the question
        />

        {/* Speichern Button */}
        <div>
            <Button
                label="Speichern + Schließen"
                icon="pi pi-save"
                onClick={onSave}
                disabled={!store.editor?.isValid} // Disable if the question is not valid
                autoFocus
            />
        </div>
    </div>
});

const TrainingQuestionEditor = observer(({ store }: { store: any }) => {
    const handleClose = () => {
        store.closeQuestionEditor(); // Close the dialog by setting the editor to null
    };

    return (
        <Dialog
            visible={!!store.editor} // Show dialog only if editor is available
            header="Frage bearbeiten"
            footer={() => (
                <TrainingQuestionEditorFooter
                    onSave={async () => {
                        store.onSaveQuestion().then((success: boolean) => {
                            if (success) {

                                handleClose(); // Close the dialog after saving
                                store.fetchTraining(); // Fetch the training data again to refresh the list    
                            }
                        });
                    }}
                    onArchived={() => { }}
                    store={store}
                />
            )}
            onHide={handleClose}
            style={{ width: '50rem' }}
        >
            {store.editor && (
                <div>
                    {/* Question Text */}
                    <div style={{ marginBottom: '1rem' }}>
                        <label htmlFor="questionText" style={{ display: 'block', marginBottom: '0.5rem' }}>
                            Fragetext
                        </label>
                        <InputText
                            id="questionText"
                            value={store.editor.question?.text || ''}
                            onChange={(e) => {
                                if (store.editor?.question) {
                                    store.editor.question.updateText(e.target.value);
                                }
                            }}
                            placeholder="Fragetext eingeben"
                            style={{ width: '100%' }}
                            data-pr-tooltip="Die Frage muss mindestens drei Zeichen lang sein."
                            data-pr-position="top"
                            data-pr-disabled={store.editor.question.isValid}
                            invalid={!store.editor.question?.isValid}
                        />
                        <Tooltip target="#questionText" />
                    </div>

                    {/* Answers Section */}
                    <div>
                        <h4>Antworten</h4>
                        {store.editor.answers?.map((answer: any, index: number) => (
                            <div
                                key={`answer-${answer.id || index}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '10px',
                                }}
                            >
                                <InputText
                                    id={`answer-text-${answer.id || index}`}
                                    value={answer.text}
                                    onChange={(e) => {
                                        answer.updateText(e.target.value);
                                    }}
                                    data-pr-tooltip="Die Antwort muss mindestens drei Zeichen lang sein."
                                    data-pr-position="top"
                                    data-pr-disabled={answer.isValid}
                                    invalid={!answer.isValid}
                                    placeholder={`Antwort ${index + 1}`}
                                    className="w-full"
                                />
                                <Tooltip target={`#answer-text-${answer.id || index}`} />
                                <Button
                                    icon={answer.isCorrect ? 'pi pi-check-circle' : 'pi pi-circle'}
                                    className={`p-button-sm ${answer.isCorrect ? 'p-button-success' : 'p-button-secondary'
                                        }`}
                                    onClick={() => answer.swtichCorrect()}
                                    tooltip={answer.isCorrect ? 'Richtige Antwort' : 'Als richtig markieren'}
                                />
                                <Button
                                    icon="pi pi-trash"
                                    className="p-button-sm p-button-danger"
                                    onClick={() => store.editor?.removeAnswer(answer.id)}
                                    tooltip="Antwort löschen"
                                />
                            </div>
                        ))}
                        {store.editor.answers?.length < 2 && (
                            <p style={{ color: 'red', fontStyle: 'italic' }}>Mindestens zwei Antworten erforderlich!</p>
                        )}
                        {!(store.editor.answers || []).some((answer: any) => answer.isCorrect) && (
                            <p style={{ color: 'red', fontStyle: 'italic' }}>Mindestens eine Antwort muss richtig sein!</p>
                        )}
                    </div>
                </div>
            )}
        </Dialog>
    );
});

const SortableQuestion = ({
    id,
    question,
    isActive,
    onClick,
    onEditQuestion,
    onDeleteQuestion
}: any) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        marginBottom: '20px',
        border: '1px solid #ccc',
        borderRadius: '8px',
        padding: '15px',
        backgroundColor: isActive ? '#f9f9f9' : '#fff',
        cursor: 'grab',
        display: 'flex',
        flexDirection: 'column' as 'column', // Top-down layout
        gap: '15px',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {/* Question Section */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Button
                        icon={isActive ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
                        className="p-button-text p-button-sm"
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()} // Prevent drag-and-drop from interfering
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent drag listeners from interfering
                            onClick();
                        }}
                    />
                    <div style={{ flex: 1 }}>
                        {question.text}
                    </div>
                </div>
            </div>

            {/* Answers Section */}
            {isActive && (
                <div style={{ textAlign: 'left' }}>
                    <h4>Antworten</h4>
                    {question.answers.map((answer: any, index: number) => (
                        <div
                            key={`question-${question.id}-answer-${answer.id || index}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                marginBottom: '10px',
                            }}
                        >
                            <InputText
                                value={answer.text}
                                placeholder={`Antwort ${index + 1}`}
                                className="w-full"
                                disabled={true}
                            />
                            <Button
                                icon={answer.isCorrect ? 'pi pi-check-circle' : 'pi pi-circle'}
                                className={`p-button-sm ${answer.isCorrect ? 'p-button-success' : 'p-button-secondary'
                                    }`}
                                disabled={true}
                                tooltip={answer.isCorrect ? 'Richtige Antwort' : 'Als richtig markieren'}
                            />
                        </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            icon="pi pi-pencil"
                            label="Frage bearbeiten"
                            className="p-button-sm p-button-secondary"
                            onMouseDown={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e: any) => {
                                e.stopPropagation();
                                onEditQuestion();
                            }}
                        />
                        <Button
                            icon="pi pi-trash"
                            label="Frage löschen"
                            className="p-button-sm p-button-danger"
                            onMouseDown={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e: any) => {
                                e.stopPropagation();
                                onDeleteQuestion();
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const TrainingQuestionCatalog = observer(({ store, sdk }: { store: any, sdk: any }) => {
    const onDragEnd = (event: any) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            store.changeOrderQuestion(active.id, over.id); // Update the order of questions in the store
        }
    };

    return (
        <div className="p-4">
            <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={store?.allQuestionOrder || []} strategy={verticalListSortingStrategy}>
                    {(store.allQuestions || []).map((q: any) => (
                        <SortableQuestion
                            key={q.id}
                            id={q.id}
                            question={q}
                            isActive={store.menuActiveQuestion === q.id}
                            onClick={() => store.setMenuActiveQuestion(q.id)}
                            onEditQuestion={() => store.openQuestionEditor(q.id)}
                            onDeleteQuestion={() => {
                                sdk.openDeleteConfirmationDialog(
                                    'Möchten Sie die Frage wirklich löschen?',
                                    'Bitte geben Sie "löschen" ein, um zu bestätigen.',
                                    (accepted: boolean) => {
                                        sdk.closeDeleteConfirmationDialog();
                                        if (accepted) {
                                            store.deleteQuestion(q.id).then((success: boolean) => success ? store.fetchTraining() : null); // Delete the question from the store
                                        }
                                    },
                                    'löschen',
                                )
                            }}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            <TrainingQuestionEditor store={store} />
        </div>
    );
})

const TrainingDialog = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const store = sdk.currentDialog || null;

    return (
        <Dialog
            visible={!!store}
            header={store.header}
            onHide={() => {
                sdk.closeDialog();
            }}
            footer={() => <DialogFooter
                onSave={async () => {
                    store.createOrUpdateTraining().then(({ id, event }: { id: number | null, event: any }) => {
                        if (event === 'NEW') {
                            sdk.showSuccessToast('Schulung erfolgreich erstellt');
                        } else if (event === 'UPDATE') {
                            sdk.showSuccessToast('Schulung erfolgreich aktualisiert');
                            sdk.closeDialog(event);
                        }
                    }).catch((error: any) => {
                        sdk.showErrorToast(error?.message || 'Unbekannter Fehler')
                    });
                }}
                store={store}
            />}
            style={{ width: store.isNewTraining ? '40rem' : '75rem' }}
        >
            {store.isLoading ? (
                <div className="flex justify-content-center align-items-center" style={{ height: '100%' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="dialog-content">
                    <div className={'left-column'}>
                        <div className={'card'}>
                            <UploadImageDialog store={store} />
                            <TrainingName store={store} />
                            <TrainingVideoUrl store={store} />
                            <TrainingDescription store={store} />
                            {!store.isNewTraining && (
                                <div
                                    id="training-active"
                                    className={`field-checkbox ${store.allQuestions?.length === 0 ? 'p-invalid' : ''}`}
                                    style={{
                                        marginTop: '1rem',
                                        border: store.allQuestions?.length === 0 ? '1px solid red' : 'none',
                                        padding: '0.5rem',
                                        borderRadius: '4px',
                                    }}
                                    data-pr-tooltip={
                                        store.allQuestions?.length === 0
                                            ? 'Es muss mindestens eine Frage vorhanden sein, um die Schulung zu aktivieren.'
                                            : ''
                                    }
                                    data-pr-position="top"
                                >
                                    <Checkbox
                                        inputId="trainingActive"
                                        checked={store.isActive}
                                        disabled={store.allQuestions?.length === 0}
                                        onChange={(e) => store.toggleActive()}
                                    />
                                    <label htmlFor="trainingActive" style={{ marginLeft: '0.5rem' }}>Schulung aktiv</label>
                                    <Tooltip target="#training-active" />
                                </div>
                            )}
                        </div>
                    </div>
                    {!store.isNewTraining && (
                        <div className={'right-column'}>
                            <div className={'card'}>
                                <TrainingQuestionCatalog sdk={sdk} store={store} />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Dialog>
    );
});

export default TrainingDialog;